// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "What Konvo knows" page as data. The shapes the API sends (konvo_api
// KonvoAssistantKnowsController, KonvoAssistantNotes, engine/EngineLearned) and the pure rules the
// page draws with: which card a note sits in, "Name: role" notes as people, what a learned entry
// says and offers, and short names for VIPs. No Lingui here, so the rules stay unit-testable;
// KnowsMessages turns the keys into words.

export const KNOWS_TOPICS = ['you', 'preferences', 'people', 'work'] as const;
export type KnowsTopic = (typeof KNOWS_TOPICS)[number];
/** chat: Konvo saved it from a conversation; you: typed on the page; legacy: from the old memory box. */
export type KnowsSource = 'chat' | 'you' | 'legacy';

export interface KnowsNote {
	id: string;
	text: string;
	topic: KnowsTopic | null;
	source: KnowsSource;
	created_at: string;
	updated_at: string;
}

export type KnowsFactKind = 'place' | 'hours' | 'language' | 'company' | 'role';

export interface KnowsSummary {
	text: string;
	facts: Array<{kind: KnowsFactKind; text: string}>;
	notes_hash: string;
	generated_at: string;
}

export type KnowsTriggerKind =
	| 'meeting_soon'
	| 'morning'
	| 'vip_mail'
	| 'task_overdue'
	| 'commitment_due'
	| 'chat_message'
	| 'waiting';

export type KnowsLearned =
	| {
			id: string;
			type: 'tuning';
			kind: KnowsTriggerKind;
			level: 'selective' | 'strict';
			ignored: number;
			unwanted: number;
	  }
	| {id: string; type: 'muted'; topic: string; title: string; until: string}
	| {id: 'vips'; type: 'vips'; names: Array<string>};

export interface KnowsPage {
	name: string;
	notes: Array<KnowsNote>;
	summary: KnowsSummary | null;
	summary_stale: boolean;
	size: number;
	max: number;
	learned: Array<KnowsLearned>;
}

export interface KnowsHistoryEntry {
	at: string;
	action: 'added' | 'changed' | 'forgot';
	text: string;
	source: KnowsSource | null;
}

export type KnowsAddError = 'empty' | 'duplicate' | 'full' | 'failed';

/** Where a note is shown: notes Konvo has not filed yet sit with "About you" until it does. */
export function displayTopic(note: Pick<KnowsNote, 'topic'>): KnowsTopic {
	return note.topic ?? 'you';
}

/** Notes per card, oldest first, so a note you just added lands at the bottom of its card. */
export function groupNotes(notes: ReadonlyArray<KnowsNote>): Record<KnowsTopic, Array<KnowsNote>> {
	const groups: Record<KnowsTopic, Array<KnowsNote>> = {you: [], preferences: [], people: [], work: []};
	for (const note of [...notes].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
		groups[displayTopic(note)].push(note);
	}
	return groups;
}

/** The desktop page's two columns (the mockup's grouping): learned sits under the left column. */
export const LEFT_TOPICS: ReadonlyArray<KnowsTopic> = ['you', 'preferences'];
export const RIGHT_TOPICS: ReadonlyArray<KnowsTopic> = ['people', 'work'];

export interface PersonNote {
	name: string;
	role: string;
	initials: string;
}

const NAME_WORD = /^[\p{Lu}][\p{L}'’.-]*$/u;
const NAME_PARTICLES = new Set(['de', 'la', 'van', 'der', 'du', 'le', 'von', 'da', 'di', 'den', 'ten', 'te']);

/**
 * "Markus Mulder: lead developer, reviews the PRs" as a person row. The part before the colon
 * must read like a name (one to four words, capitalised apart from particles such as "de la");
 * anything else ("Summaries: decisions first") stays a plain line.
 */
export function parsePersonNote(text: string): PersonNote | null {
	const match = /^([^:]{1,60}):\s*(.+)$/u.exec(text.trim());
	if (!match) return null;
	const name = match[1].trim().replace(/\s+/g, ' ');
	const role = match[2].trim();
	const words = name.split(' ');
	if (!role || words.length > 5) return null;
	if (!NAME_WORD.test(words[0])) return null;
	if (!words.every((word) => NAME_WORD.test(word) || NAME_PARTICLES.has(word.toLowerCase()))) return null;
	return {name, role: sentenceCase(role), initials: initialsOf(name)};
}

/** "lead developer" -> "Lead developer"; words with their own capitals ("iCAM") stay as they are. */
function sentenceCase(text: string): string {
	const first = text.split(/[\s,;]/)[0] ?? '';
	if (first !== first.toLocaleLowerCase()) return text;
	return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

/** "Markus Mulder" -> "MM", "JP de la Rey" -> "JR", "Gustav" -> "GU". */
export function initialsOf(name: string): string {
	const words = name
		.split(/\s+/)
		.filter((word) => word && !NAME_PARTICLES.has(word.toLowerCase()))
		.map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
		.filter(Boolean);
	if (words.length === 0) return '?';
	if (words.length === 1) return words[0].slice(0, 2).toLocaleUpperCase();
	return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toLocaleUpperCase();
}

/** A stable hue (0-359) for a name, for the people avatars. */
export function nameHue(name: string): number {
	let hash = 0;
	for (const char of name.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
	return hash % 360;
}

/** A VIP entry ("gustav.bosman@x.co.za", "@discovery.co.za") as a short name: "Gustav", "Discovery". */
export function vipShortName(entry: string): string {
	const value = entry.trim();
	const at = value.indexOf('@');
	if (at === -1) return value;
	const local = value.slice(0, at);
	const domain = value.slice(at + 1);
	const word = local ? local.split(/[._+-]/)[0] : domain.split('.')[0];
	if (!word) return value;
	return word.charAt(0).toLocaleUpperCase() + word.slice(1);
}

export type LearnedAction = 'reset' | 'undo' | 'edit';

export type LearnedView =
	| {type: 'tuning'; kind: KnowsTriggerKind; detail: 'ignored' | 'unwanted'; count: number; action: 'reset'}
	| {type: 'muted'; title: string; until: string; action: 'undo'}
	| {type: 'vips'; names: Array<string>; action: 'edit'};

/** What a learned entry says: the sub line counts what made Konvo stricter (marked unwanted wins). */
export function learnedView(entry: KnowsLearned): LearnedView {
	switch (entry.type) {
		case 'tuning':
			return entry.unwanted > 0
				? {type: 'tuning', kind: entry.kind, detail: 'unwanted', count: entry.unwanted, action: 'reset'}
				: {type: 'tuning', kind: entry.kind, detail: 'ignored', count: entry.ignored, action: 'reset'};
		case 'muted':
			return {type: 'muted', title: entry.title, until: entry.until, action: 'undo'};
		case 'vips':
			return {type: 'vips', names: dedupe(entry.names.map(vipShortName)), action: 'edit'};
	}
}

function dedupe(values: ReadonlyArray<string>): Array<string> {
	const seen = new Set<string>();
	return values.filter((value) => {
		const key = value.toLowerCase();
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

/** The hover line's kind: legacy notes have no meaningful date (they were split from the old box). */
export function noteOrigin(note: Pick<KnowsNote, 'source'>): 'chat' | 'you' | 'before' {
	return note.source === 'legacy' ? 'before' : note.source;
}
